/*
 * A small key -> bytes store on the device, in IndexedDB: the offline base
 * map's byte ranges (utils/offline/vector.js). With no IndexedDB (a private
 * window that refuses it, the tests) it is memory for this page only.
 *
 *   get(key) -> ArrayBuffer | null     put(key, bytes)     clear()     usage() -> {entries, bytes}
 */
const DB = 'pitriot-offline';
const STORE = 'basemap';

function openDb(idb) {
  return new Promise((resolve, reject) => {
    const req = idb.open(DB, 1);
    req.onupgradeneeded = () => { if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// The request's own result, never the request: a key that is not there reads
// as `undefined`, and resolving to the IDBRequest instead (as `?? out` once
// did) made every missing range look kept, so the base map never loaded.
function run(db, mode, work) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = work(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(request ? request.result : undefined);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export const isQuota = (e) => e?.name === 'QuotaExceededError' || e?.code === 22;

export function memoryByteStore() {
  const data = new Map();
  return {
    async get(key) { return data.get(key) || null; },
    async put(key, bytes) { data.set(key, bytes); },
    async clear() { data.clear(); },
    async usage() {
      let bytes = 0;
      for (const v of data.values()) bytes += v.byteLength;
      return { entries: data.size, bytes };
    },
  };
}

export function deviceByteStore(idb = typeof indexedDB !== 'undefined' ? indexedDB : null) {
  if (!idb) return memoryByteStore();
  let dbp = null;
  const db = () => { dbp = dbp || openDb(idb); return dbp; };
  const fallback = memoryByteStore();
  // A full disk is said, not hidden: saving an area must know it did not fit.
  const safe = (fn, otherwise) => async (...args) => {
    try {
      return await fn(await db(), ...args);
    } catch (e) {
      if (isQuota(e)) throw e;
      return otherwise(...args);
    }
  };
  return {
    get: safe((d, key) => run(d, 'readonly', (s) => s.get(key)).then((v) => v || null), fallback.get),
    put: safe((d, key, bytes) => run(d, 'readwrite', (s) => { s.put(bytes, key); }), fallback.put),
    clear: safe((d) => run(d, 'readwrite', (s) => { s.clear(); }), fallback.clear),
    usage: safe(async (d) => {
      const values = await run(d, 'readonly', (s) => s.getAll());
      let bytes = 0;
      for (const v of values || []) bytes += v?.byteLength || 0;
      return { entries: (values || []).length, bytes };
    }, fallback.usage),
  };
}
